import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Check, ChevronDown, ChevronUp, Search, X, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color filter categories with their hex ranges
const COLOR_FILTERS = [
  { id: 'white', name: 'White', color: '#FFFFFF' },
  { id: 'grey', name: 'Grey', color: '#808080' },
  { id: 'black', name: 'Black', color: '#1A1A1A' },
  { id: 'brown', name: 'Brown', color: '#7B3F00' },
  { id: 'red', name: 'Red', color: '#DC2626' },
  { id: 'orange', name: 'Orange', color: '#EA580C' },
  { id: 'yellow', name: 'Yellow', color: '#EAB308' },
  { id: 'green', name: 'Green', color: '#16A34A' },
  { id: 'blue', name: 'Blue', color: '#2563EB' },
  { id: 'purple', name: 'Purple', color: '#7C3AED' },
  { id: 'pink', name: 'Pink', color: '#EC4899' },
];

// Helper function to categorize a hex color
const categorizeColor = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const l = (max + min) / 2;
  const d = max - min;
  
  let h = 0, s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rNorm) h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
    else if (max === gNorm) h = ((bNorm - rNorm) / d + 2) / 6;
    else h = ((rNorm - gNorm) / d + 4) / 6;
  }
  
  const hue = h * 360;
  const saturation = s * 100;
  const lightness = l * 100;
  
  // White
  if (lightness > 85 && saturation < 20) return 'white';
  // Black
  if (lightness < 15) return 'black';
  // Grey (low saturation, mid lightness)
  if (saturation < 15 && lightness >= 15 && lightness <= 85) return 'grey';
  // Brown (dark warm tones)
  if (lightness < 45 && saturation > 15 && hue >= 0 && hue <= 45) return 'brown';
  // Chromatic colors by hue
  if (hue >= 0 && hue < 15) return 'red';
  if (hue >= 15 && hue < 40) return 'orange';
  if (hue >= 40 && hue < 70) return 'yellow';
  if (hue >= 70 && hue < 170) return 'green';
  if (hue >= 170 && hue < 260) return 'blue';
  if (hue >= 260 && hue < 300) return 'purple';
  if (hue >= 300 && hue < 340) return 'pink';
  if (hue >= 340 && hue <= 360) return 'red';
  
  return 'grey';
};

// Fallback fabric images for ranges without scraped banner
// Generic fallback when no /collections/ page exists AND no 2nd product image was found
const DEFAULT_RANGE_IMAGE = '/default-range-image.jpg';

const FabricStep = ({ 
  selectedRange, 
  selectedColor, 
  onRangeSelect, 
  onColorSelect,
  onSearchFabricSelect,
  cushionCategory,
  fabricZoom,
  onFabricZoomChange,
  defaultExpanded
}) => {
  const [fabricTypes, setFabricTypes] = useState([]);
  const [fabricRanges, setFabricRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [activeColorFilter, setActiveColorFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [expandedRange, setExpandedRange] = useState(defaultExpanded || null);
  const [hoveredColor, setHoveredColor] = useState(null);
  const [hoveredPosition, setHoveredPosition] = useState({ top: 0, left: 0 });
  const [mobileActiveColor, setMobileActiveColor] = useState(null);
  const [mobilePosition, setMobilePosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [showAllRanges, setShowAllRanges] = useState(false);
  const scrollRef = useRef(null);
  const typeScrollRef = useRef(null);
  const colorScrollRef = useRef(null);
  const [typeScrolledEnd, setTypeScrolledEnd] = useState(true);
  const [colorScrolledEnd, setColorScrolledEnd] = useState(true);

  // Warwick search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [expandedSearchRange, setExpandedSearchRange] = useState(null);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle scroll to dismiss mobile color preview
  useEffect(() => {
    if (!isMobile || !mobileActiveColor) return;
    
    const handleScroll = () => {
      setMobileActiveColor(null);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, mobileActiveColor]);

  // Track scroll position for gradient fade on filter rows
  const checkScrollEnd = useCallback((el, setter) => {
    if (!el) return;
    const atEnd = el.scrollWidth - el.clientWidth - el.scrollLeft < 5;
    const noOverflow = el.scrollWidth <= el.clientWidth;
    setter(atEnd || noOverflow);
  }, []);

  const handleTypeScroll = useCallback(() => checkScrollEnd(typeScrollRef.current, setTypeScrolledEnd), [checkScrollEnd]);
  const handleColorScroll = useCallback(() => checkScrollEnd(colorScrollRef.current, setColorScrolledEnd), [checkScrollEnd]);

  // Re-check on filter content changes and on mount
  useEffect(() => {
    handleTypeScroll();
    handleColorScroll();
    // Also observe resize changes on the scroll containers
    const ro = new ResizeObserver(() => { handleTypeScroll(); handleColorScroll(); });
    if (typeScrollRef.current) ro.observe(typeScrollRef.current);
    if (colorScrollRef.current) ro.observe(colorScrollRef.current);
    return () => ro.disconnect();
  }, [activeTypeFilter, activeColorFilter, fabricTypes, handleTypeScroll, handleColorScroll]);

  // Fetch fabric types and ranges
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [typesRes, rangesRes] = await Promise.all([
          axios.get(`${API}/fabric-types`),
          axios.get(`${API}/fabric-ranges`, { 
            params: { 
              category: cushionCategory,
              sort_by: sortBy 
            } 
          })
        ]);
        setFabricTypes(typesRes.data);
        setFabricRanges(rangesRes.data);
      } catch (error) {
        console.error('Error fetching fabric data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cushionCategory, sortBy]);

  // Autocomplete suggestions as user types
  // Preload all fabric images as blob URLs for instant display
  const getCachedUrl = (url) => url;

  // Add color categories to each color in ranges
  // Use server-provided color_category when available, fallback to HSL analysis
  // Determine if this is an outdoor cushion
  const isOutdoorCushion = (cushionCategory || '').split(',').includes('outdoor');

  const rangesWithColorCategories = useMemo(() => {
    return fabricRanges.map(range => ({
      ...range,
      colors: range.colors.map(color => ({
        ...color,
        colorCategory: color.color_category || categorizeColor(color.hex_color)
      }))
    }));
  }, [fabricRanges]);

  // Base ranges filtered by category only (keeps DOM alive for instant filter switching)
  const categoryFilteredRanges = useMemo(() => {
    let ranges = rangesWithColorCategories;
    if (isOutdoorCushion) {
      ranges = ranges.filter(r => (r.fabric_types || []).includes('outdoor'));
    } else {
      ranges = ranges.filter(r => !(r.fabric_types || []).includes('outdoor'));
    }
    return ranges;
  }, [rangesWithColorCategories, isOutdoorCushion]);

  // IDs of ranges that pass all filters (type + color)
  const visibleRangeIds = useMemo(() => {
    let ranges = categoryFilteredRanges;
    
    // Filter by fabric type
    if (activeTypeFilter !== 'all') {
      if (activeTypeFilter === 'plain' && !isOutdoorCushion) {
        ranges = ranges.filter(r => {
          const types = (r.fabric_types || []).filter(t => t !== 'textured');
          return types.length === 1 && types[0] === 'plain';
        });
      } else {
        ranges = ranges.filter(r => (r.fabric_types || []).includes(activeTypeFilter));
      }
    }
    
    // Filter by color
    if (activeColorFilter !== 'all') {
      ranges = ranges.filter(range => 
        range.colors.some(c => c.colorCategory === activeColorFilter)
      );
    }
    
    return new Set(ranges.map(r => r.id));
  }, [categoryFilteredRanges, activeTypeFilter, activeColorFilter, isOutdoorCushion]);

  // Reset "Load All" when filters change
  useEffect(() => { setShowAllRanges(false); }, [activeTypeFilter, activeColorFilter, sortBy]);

  // Filtered colors per range (for color filter)
  const filteredColorsByRange = useMemo(() => {
    if (activeColorFilter === 'all') return {};
    const map = {};
    for (const range of categoryFilteredRanges) {
      map[range.id] = range.colors.filter(c => c.colorCategory === activeColorFilter);
    }
    return map;
  }, [categoryFilteredRanges, activeColorFilter]);

  // Get selected range and color objects
  const selectedRangeObj = useMemo(() => 
    fabricRanges.find(r => r.id === selectedRange), 
    [fabricRanges, selectedRange]
  );
  
  const selectedColorObj = useMemo(() => 
    selectedRangeObj?.colors.find(c => c.id === selectedColor),
    [selectedRangeObj, selectedColor]
  );

  // Compute selected search fabric from search results when a warwick-search range is selected
  const selectedSearchInfo = useMemo(() => {
    if (!selectedRange?.startsWith('warwick-search-') || !searchResults?.ranges) return null;
    for (const range of searchResults.ranges) {
      const searchRangeId = `warwick-search-${range.range_slug}`;
      if (searchRangeId === selectedRange) {
        const color = range.colors.find(c => `${searchRangeId}-${c.slug}` === selectedColor);
        if (color) return { range, color };
      }
    }
    return null;
  }, [selectedRange, selectedColor, searchResults]);

  const handleMouseEnter = (colorId, e) => {
    if (isMobile) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredPosition({
      top: rect.top,
      left: rect.left + rect.width / 2
    });
    setHoveredColor(colorId);
  };

  const handleRangeClick = (rangeId) => {
    if (expandedRange === rangeId) {
      setExpandedRange(null);
    } else {
      setExpandedRange(rangeId);
    }
  };

  const handleWarwickSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResults(null);
    try {
      const res = await axios.post(`${API}/warwick-search`, { query: searchQuery.trim() });
      if (res.data.status === 'success' && res.data.ranges?.length > 0) {
        setSearchResults(res.data);
      } else if (res.data.status === 'success' && res.data.ranges?.length === 0) {
        setSearchError(`No results found for "${searchQuery}". Try searching by range name (e.g. Beachcomber, Adra, Mystere).`);
      } else if (res.data.status === 'no_results') {
        setSearchError(`No results found for "${searchQuery}"`);
      } else {
        setSearchError(res.data.error || 'Search failed. Please try again.');
      }
    } catch (err) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchColorSelect = (color, range) => {
    if (color.in_stock === false) return;
    const searchRangeId = `warwick-search-${range.range_slug}`;
    const searchColorId = `${searchRangeId}-${color.slug}`;
    onRangeSelect(searchRangeId);
    onColorSelect(searchColorId);
    if (onSearchFabricSelect) {
      onSearchFabricSelect({
        rangeId: searchRangeId,
        rangeName: range.range_name,
        colorId: searchColorId,
        colorName: color.name,
        fullName: color.full_name,
        imageUrl: color.image_url,
        pricePerMeter: range.retail_price,
      });
    }
    if (isMobile) setMobileActiveColor(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleColorSelect = (rangeId, colorId) => {
    onRangeSelect(rangeId);
    onColorSelect(colorId);
    if (onSearchFabricSelect) onSearchFabricSelect(null);
    if (isMobile) {
      setMobileActiveColor(null);
    }
    // Scroll to top after selection
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleColorInteraction = (colorId, e) => {
    if (isMobile) {
      e.stopPropagation();
      if (mobileActiveColor === colorId) {
        setMobileActiveColor(null);
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        setMobilePosition({
          top: rect.top,
          left: rect.left + rect.width / 2
        });
        setMobileActiveColor(colorId);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="fabric-loading">
        <div className="animate-spin h-8 w-8 border-4 border-bush border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="step-fabric-new" className="space-y-4 overflow-x-hidden" ref={scrollRef}>
      {/* Header with Sort */}
      <div className={`flex items-start justify-between gap-4 ${(selectedRangeObj && selectedColorObj) || selectedSearchInfo ? 'mb-4' : ''}`}>
        <div>
          <h2 className="text-lg font-semibold text-[#1A1A1A] mb-1">
            {(selectedRangeObj && selectedColorObj) || selectedSearchInfo ? 'Selected Fabric' : isOutdoorCushion ? 'Choose Your Outdoor Fabric' : 'Choose Your Fabric'}
          </h2>
          {!((selectedRangeObj && selectedColorObj) || selectedSearchInfo) && (
            <p className="text-gray-600 text-sm">{isOutdoorCushion ? 'Select an outdoor fabric range and then choose a colour.' : 'Select a fabric range, then choose your colour.'}</p>
          )}
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-28 sm:w-32 h-8 sm:h-9 text-xs sm:text-sm flex-shrink-0" data-testid="fabric-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="price_low">Price: Low</SelectItem>
            <SelectItem value="price_high">Price: High</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Selected Fabric Summary */}
      {selectedRangeObj && selectedColorObj && (
        <div className="bg-bush/5 border border-bush/20 rounded-xl overflow-hidden" data-testid="selected-fabric-summary">
          <div className="flex">
            {selectedColorObj.image_url && (selectedColorObj.image_url.includes('warwick.com.au') || selectedColorObj.image_url.startsWith('/api/fabric-images')) ? (
              <img 
                src={getCachedUrl(selectedColorObj.image_url)} 
                alt={selectedColorObj.name}
                className="w-1/2 aspect-square flex-shrink-0 object-cover"
              />
            ) : (
              <div 
                className="w-1/2 aspect-square flex-shrink-0"
                style={{ backgroundColor: selectedColorObj.hex_color }}
              />
            )}
            <div className="w-1/2 p-4 flex flex-col justify-center">
              <p className="font-semibold text-[#1A1A1A] text-sm sm:text-base">{selectedColorObj.name}</p>
              <p className="text-xs sm:text-sm text-gray-500">{selectedRangeObj.name}</p>
              <p className="text-sm sm:text-base font-medium text-bush mt-1">${Math.round(selectedRangeObj.price_per_meter)}/meter</p>
              {(selectedRangeObj.composition || selectedRangeObj.martindale || selectedRangeObj.pattern_repeat || selectedRangeObj.pattern_match) && (
                <div className="flex flex-col gap-0.5 mt-2 text-xs sm:text-sm text-gray-500">
                  {selectedRangeObj.composition && <p><span className="font-medium text-gray-600">Composition:</span> {selectedRangeObj.composition}</p>}
                  {selectedRangeObj.martindale && <p><span className="font-medium text-gray-600">Martindale:</span> {selectedRangeObj.martindale}</p>}
                  {selectedRangeObj.pattern_repeat && <p><span className="font-medium text-gray-600">Pattern Repeat:</span> {selectedRangeObj.pattern_repeat}</p>}
                  {selectedRangeObj.pattern_match && <p><span className="font-medium text-gray-600">Pattern Match:</span> {selectedRangeObj.pattern_match}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected Search Fabric Summary */}
      {!selectedRangeObj && selectedSearchInfo && (
        <div className="bg-bush/5 border border-bush/20 rounded-xl overflow-hidden" data-testid="selected-fabric-summary">
          <div className="flex">
            {selectedSearchInfo.color.image_url ? (
              <img 
                src={selectedSearchInfo.color.image_url} 
                alt={selectedSearchInfo.color.name}
                className="w-1/2 aspect-square flex-shrink-0 object-cover"
              />
            ) : (
              <div className="w-1/2 aspect-square flex-shrink-0 bg-gray-200" />
            )}
            <div className="w-1/2 p-4 flex flex-col justify-center">
              <p className="font-semibold text-[#1A1A1A] text-sm sm:text-base">{selectedSearchInfo.color.name}</p>
              <p className="text-xs sm:text-sm text-gray-500">{selectedSearchInfo.range.range_name}</p>
              {selectedSearchInfo.range.retail_price > 0 && (
                <p className="text-sm sm:text-base font-medium text-bush mt-1">${Math.round(selectedSearchInfo.range.retail_price)}/meter</p>
              )}
              {selectedSearchInfo.color.in_stock === false && (
                <p className="text-xs text-red-500 font-medium mt-1">Out of stock</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters Row */}
      <div className="space-y-3">
        {/* Type Filter Tabs */}
        <div className="relative">
          <div className="overflow-x-auto pb-1 scrollbar-hide" ref={typeScrollRef} onScroll={handleTypeScroll}>
            <Tabs value={activeTypeFilter} onValueChange={setActiveTypeFilter}>
              <TabsList className="h-8 sm:h-9 bg-sand p-0.5 w-max">
                <TabsTrigger 
                  value="all" 
                  className="text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded whitespace-nowrap"
                  data-testid="filter-all"
                >
                  All Types
                </TabsTrigger>
                {fabricTypes
                  .filter(type => isOutdoorCushion 
                    ? !['velvet', 'genuine-leather', 'boucle', 'linen', 'suede', 'outdoor'].includes(type.id)
                    : !['outdoor'].includes(type.id))
                  .map(type => (
                  <TabsTrigger 
                    key={type.id}
                    value={type.id} 
                    className="text-xs sm:text-sm px-2 sm:px-3 h-7 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded whitespace-nowrap"
                    data-testid={`filter-${type.id}`}
                  >
                    {type.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          {!typeScrolledEnd && <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none sm:hidden" />}
        </div>

        {/* Color Filter Squares */}
        <div className="relative" data-testid="color-filters">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 flex-shrink-0">Colour:</span>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1 scrollbar-hide" ref={colorScrollRef} onScroll={handleColorScroll}>
              <button
                onClick={() => setActiveColorFilter('all')}
                className={`w-6 h-6 flex-shrink-0 rounded-md border-2 transition-all flex items-center justify-center text-[10px] font-medium ${
                  activeColorFilter === 'all' 
                    ? 'border-bush ring-2 ring-bush/30' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{ 
                  background: 'linear-gradient(135deg, #ef4444 0%, #f97316 14%, #eab308 28%, #22c55e 42%, #3b82f6 57%, #8b5cf6 71%, #ec4899 85%, #ef4444 100%)' 
                }}
                title="All Colours"
                data-testid="color-filter-all"
              />
              {COLOR_FILTERS.map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveColorFilter(activeColorFilter === filter.id ? 'all' : filter.id)}
                  className={`w-6 h-6 flex-shrink-0 rounded-md border-2 transition-all ${
                    activeColorFilter === filter.id 
                      ? 'border-bush ring-2 ring-bush/30 scale-110' 
                      : 'border-gray-300 hover:border-gray-400 hover:scale-105'
                  }`}
                  style={{ 
                    backgroundColor: filter.color,
                    boxShadow: filter.id === 'white' ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : undefined
                  }}
                  title={filter.name}
                  data-testid={`color-filter-${filter.id}`}
                />
              ))}
            </div>
          </div>
          {!colorScrolledEnd && <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none sm:hidden" />}
        </div>
      </div>

      {/* Warwick Search - hidden for outdoor products */}
      {!isOutdoorCushion && <div className="border border-dashed border-teal-200 rounded-xl p-2 sm:p-3 bg-teal-50/30 mt-4 mb-2" data-testid="warwick-search-section">
        <button
          onClick={() => setShowSearch(!showSearch)}
          className="w-full flex items-center justify-between text-left"
          data-testid="warwick-search-toggle"
        >
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-teal-600" />
            <span className="text-xs sm:text-sm font-semibold text-teal-700">Search Warwick Fabrics</span>
            <span className="text-xs text-gray-400 hidden sm:inline">Find any fabric range from Warwick's full catalogue</span>
          </div>
          {showSearch ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {showSearch && (
          <div className="mt-3 space-y-3">
            <form onSubmit={handleWarwickSearch} className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by range or colour name (e.g. Mystere, Ink, Keylargo...)"
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                  data-testid="warwick-search-input"
                  autoComplete="off"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setSearchResults(null); setSearchError(''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={searchLoading || searchQuery.trim().length < 2}
                className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                data-testid="warwick-search-submit"
              >
                {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </button>
            </form>

            {searchLoading && (
              <div className="flex items-center justify-center py-8 gap-3" data-testid="warwick-search-loading">
                <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                <span className="text-sm text-gray-500">Searching Warwick catalogue... this may take a moment</span>
              </div>
            )}

            {searchError && (
              <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="warwick-search-error">
                {searchError}
              </div>
            )}

            {searchResults && searchResults.status === 'success' && (
              <div className="space-y-3" data-testid="warwick-search-results">
                {searchResults.from_cache && (
                  <p className="text-xs text-gray-400 mb-2 hidden">Cached results — prices updated every 24 hours</p>
                )}
                {searchResults.ranges.map((range) => {
                  const searchRangeId = `warwick-search-${range.range_slug}`;
                  const isExpanded = expandedSearchRange === searchRangeId;
                  return (
                    <div 
                      key={range.range_slug}
                      className={`border-2 rounded-xl overflow-hidden transition-all ${
                        selectedRange === searchRangeId ? 'border-bush bg-bush/5' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`search-range-${range.range_slug}`}
                    >
                      {/* Range Header */}
                      <button
                        onClick={() => setExpandedSearchRange(isExpanded ? null : searchRangeId)}
                        className="w-full p-4 flex items-center gap-4 text-left hover:bg-gray-50/50 transition-colors"
                      >
                        {/* Color Preview Stack */}
                        <div className="relative w-14 h-14 flex-shrink-0">
                          {range.colors.slice(0, 4).map((color, i) => (
                            <div
                              key={color.slug}
                              className="absolute w-10 h-10 rounded-lg shadow-sm border border-white overflow-hidden"
                              style={{ top: `${i * 3}px`, left: `${i * 3}px`, zIndex: 4 - i }}
                            >
                              {color.image_url ? (
                                <img src={color.image_url} alt={color.name} loading="lazy" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-gray-200" />
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#1A1A1A]">{range.range_name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            {range.retail_price > 0 && (
                              <span className="text-sm font-medium text-bush">${Math.round(range.retail_price)}/m</span>
                            )}
                    <span className="hidden sm:inline text-xs text-gray-400">{range.color_count} colours</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                        </div>
                      </button>

                      {/* Expanded Colors Grid */}
                      <div 
                        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                      >
                        <div className="overflow-hidden">
                          <div className="border-t bg-white p-3 sm:p-4">
                            <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">Select a colour:</p>
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 sm:gap-3">
                              {range.colors.map((color) => {
                                const searchColorId = `${searchRangeId}-${color.slug}`;
                                const isHovered = !isMobile && hoveredColor === searchColorId;
                                const isMobileActive = isMobile && mobileActiveColor === searchColorId;
                                const showPreview = isHovered || isMobileActive;
                                const isOutOfStock = color.in_stock === false;
                                const isSelected = selectedRange === searchRangeId && selectedColor === searchColorId;

                                return (
                                  <div key={color.slug} className="relative">
                                    <button
                                      onClick={(e) => {
                                        if (isMobile) {
                                          if (mobileActiveColor !== searchColorId) {
                                            handleColorInteraction(searchColorId, e);
                                          } else if (!isOutOfStock) {
                                            handleSearchColorSelect(color, range);
                                          }
                                        } else if (!isOutOfStock) {
                                          handleSearchColorSelect(color, range);
                                        }
                                      }}
                                      onMouseEnter={(e) => handleMouseEnter(searchColorId, e)}
                                      onMouseLeave={() => !isMobile && setHoveredColor(null)}
                                      className={`group w-full flex flex-col items-center p-2 rounded-lg transition-all ${
                                        isOutOfStock
                                          ? 'cursor-not-allowed'
                                          : isSelected
                                            ? 'bg-bush/10 ring-2 ring-bush'
                                            : 'hover:bg-gray-100'
                                      }`}
                                      data-testid={`warwick-color-${color.slug}`}
                                    >
                                      <div 
                                        className={`w-16 h-16 rounded-lg shadow-md border-2 transition-transform relative overflow-hidden ${
                                          isOutOfStock ? 'border-gray-200'
                                            : isSelected ? 'border-bush group-hover:scale-105'
                                            : 'border-white group-hover:scale-105'
                                        }`}
                                      >
                                        {color.image_url ? (
                                          <img src={color.image_url} alt={color.name} loading="lazy" className="w-full h-full object-cover" />
                                        ) : (
                                          <div className="w-full h-full bg-gray-200" />
                                        )}
                                        {isSelected && !isOutOfStock && (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow">
                                              <Check className="h-4 w-4 text-bush" />
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <span className={`text-[10px] sm:text-xs text-center mt-1.5 leading-tight ${
                                        isOutOfStock ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        {color.name}
                                      </span>
                                      {isOutOfStock && (
                                        <span className="text-[8px] sm:text-[9px] text-red-500 font-medium leading-tight mt-0.5">Out of stock</span>
                                      )}
                                    </button>

                                    {/* Large Preview on Hover/Click */}
                                    {showPreview && (
                                      <div 
                                        className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
                                        style={{
                                          top: `${(isMobile ? mobilePosition.top : hoveredPosition.top) - 200}px`,
                                          left: `${isMobile ? mobilePosition.left : hoveredPosition.left}px`,
                                          transform: 'translateX(-50%)',
                                          width: '180px'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {color.image_url ? (
                                          <img src={color.image_url} alt={color.name} loading="lazy" className="w-full h-32 object-cover" />
                                        ) : (
                                          <div className="w-full h-32 bg-gray-200" />
                                        )}
                                        <div className="p-3">
                                          <p className="font-semibold text-sm text-[#1A1A1A]">{color.name}</p>
                                          <p className="text-xs text-gray-500 mb-2">{range.range_name}</p>
                                          {isOutOfStock && (
                                            <p className="text-xs text-red-500 font-medium">Out of stock</p>
                                          )}
                                          {isMobile && !isOutOfStock && (
                                            <button
                                              onClick={() => handleSearchColorSelect(color, range)}
                                              className="w-full bg-bush text-white text-xs py-2 rounded-lg font-medium"
                                            >
                                              Select
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Fabric Ranges List */}
      <div className="space-y-2 sm:space-y-2" data-testid="fabric-ranges-list">
        {visibleRangeIds.size === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No fabrics found matching your criteria.</p>
            <button 
              onClick={() => { setActiveTypeFilter('all'); setActiveColorFilter('all'); }}
              className="text-bush hover:underline mt-2"
            >
              Clear filters
            </button>
          </div>
        ) : null}
        {(() => {
          const INITIAL_COUNT = 12;
          let visibleCount = 0;
          const totalVisible = categoryFilteredRanges.filter(r => visibleRangeIds.has(r.id)).length;
          const items = [];

          for (const range of categoryFilteredRanges) {
            const isVisible = visibleRangeIds.has(range.id);
            if (!isVisible) continue;
            visibleCount++;
            if (!showAllRanges && visibleCount > INITIAL_COUNT) continue;
          const displayColors = activeColorFilter !== 'all' && filteredColorsByRange[range.id] 
            ? filteredColorsByRange[range.id] 
            : range.colors;
          items.push(
            <div 
              key={range.id}
              className={`border-2 rounded-xl overflow-hidden transition-all ${
                selectedRange === range.id ? 'border-bush bg-bush/5' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`fabric-range-${range.id}`}
            >
              {/* Range Header */}
              <button
                onClick={() => handleRangeClick(range.id)}
                className="w-full p-2.5 sm:p-3 flex items-center gap-3 text-left hover:bg-gray-50/50 transition-colors"
                data-testid={`fabric-range-header-${range.id}`}
              >
                {/* Color Preview Stack */}
                <div className="relative w-12 h-12 sm:w-12 sm:h-12 flex-shrink-0">
                  {range.colors.filter(c => c.in_stock !== false).slice(0, 4).map((color, i) => {
                    const hasRealImg = color.image_url && (color.image_url.includes('warwick.com.au') || color.image_url.startsWith('/api/fabric-images'));
                    return (
                      <div
                        key={color.id}
                        className="absolute w-9 h-9 rounded-lg shadow-sm border border-white overflow-hidden"
                        style={{
                          backgroundColor: hasRealImg ? undefined : color.hex_color,
                          top: `${i * 2}px`,
                          left: `${i * 2}px`,
                          zIndex: 4 - i
                        }}
                      >
                        {hasRealImg && (
                          <img src={getCachedUrl(color.image_url)} alt={color.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Range Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#1A1A1A]">{range.name}</h3>
                    {range.is_popular && (
                      <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm font-medium text-bush">${Math.round(range.price_per_meter)}/m</span>
                    <span className="hidden sm:inline text-xs text-gray-500">{range.colors.length} colours</span>
                    <span className="text-xs text-[#1A1A1A] capitalize">{(range.fabric_types || []).length > 0 && <>Type: {(() => { const types = (range.fabric_types || []).filter(t => t !== 'textured' && (isOutdoorCushion || t !== 'outdoor')); const display = types.length > 1 ? types.filter(t => t !== 'plain') : types; return display.map(t => t === 'vinyl-faux-leather' ? 'Vinyl/Faux Leather' : t).join(', '); })()}</>}</span>
                  </div>
                </div>

                {/* Expand/Collapse Icon */}
                <div className="flex-shrink-0">
                  {expandedRange === range.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Content - Animated */}
              <div 
                className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                style={{ gridTemplateRows: expandedRange === range.id ? '1fr' : '0fr' }}
              >
                <div className="overflow-hidden">
                {expandedRange === range.id && (
                <div className="border-t bg-white" data-testid={`fabric-colors-${range.id}`}>
                  {/* Range Detail Section - Image (1/3) + Content (2/3) */}
                  <div className="p-3 sm:p-4 border-b bg-sand/30">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      {/* Image - smaller on mobile */}
                      <div className="sm:w-1/3 flex-shrink-0">
                        <img 
                          src={range.collection_image_url || range.banner_image_url || DEFAULT_RANGE_IMAGE}
                          loading="lazy"
                          alt={`${range.name} fabric texture`}
                          onError={(e) => {
                            if (e.currentTarget.src !== window.location.origin + DEFAULT_RANGE_IMAGE) {
                              e.currentTarget.src = DEFAULT_RANGE_IMAGE;
                            }
                          }}
                          className="w-full h-32 sm:h-40 md:h-48 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      
                      {/* Content - 2/3 width on desktop */}
                      <div className="sm:w-2/3 min-w-0">
                        <h4 className="font-semibold text-base sm:text-lg text-[#1A1A1A]">{range.name} Collection</h4>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{range.description}</p>
                        {(range.composition || range.martindale || range.pattern_repeat || range.pattern_match) && (
                          <div className="flex flex-col gap-0.5 mt-2 text-xs sm:text-sm text-gray-500">
                            {range.composition && <p><span className="font-medium text-gray-600">Composition:</span> {range.composition}</p>}
                            {range.martindale && <p><span className="font-medium text-gray-600">Martindale:</span> {range.martindale}</p>}
                            {range.pattern_repeat && <p><span className="font-medium text-gray-600">Pattern Repeat:</span> {range.pattern_repeat}</p>}
                            {range.pattern_match && <p><span className="font-medium text-gray-600">Pattern Match:</span> {range.pattern_match}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Colors Grid */}
                  <div className="p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">Select a colour:</p>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-1 sm:gap-1.5">
                      {displayColors.map((color) => {
                        const isHovered = !isMobile && hoveredColor === color.id;
                        const isMobileActive = isMobile && mobileActiveColor === color.id;
                        const showPreview = isHovered || isMobileActive;
                        const isOutOfStock = color.in_stock === false;
                        const hasRealImage = color.image_url && (color.image_url.includes('warwick.com.au') || color.image_url.startsWith('/api/fabric-images'));
                        
                        return (
                          <div 
                            key={color.id}
                            className="relative"
                          >
                            <button
                              onClick={(e) => {
                                if (isMobile) {
                                  if (mobileActiveColor !== color.id) {
                                    handleColorInteraction(color.id, e);
                                  } else if (!isOutOfStock) {
                                    handleColorSelect(range.id, color.id);
                                  }
                                } else if (!isOutOfStock) {
                                  handleColorSelect(range.id, color.id);
                                }
                              }}
                              onMouseEnter={(e) => handleMouseEnter(color.id, e)}
                              onMouseLeave={() => !isMobile && setHoveredColor(null)}
                              className={`group w-full flex flex-col items-center p-2 rounded-lg transition-all ${
                                isOutOfStock
                                  ? 'cursor-not-allowed'
                                  : selectedRange === range.id && selectedColor === color.id
                                    ? 'bg-bush/10 ring-2 ring-bush'
                                    : 'hover:bg-gray-100'
                              }`}
                              data-testid={`fabric-color-${color.id}`}
                            >
                              {/* Color Swatch */}
                              <div 
                                className={`w-16 h-16 rounded-lg shadow-md border-2 transition-transform relative overflow-hidden ${
                                  isOutOfStock
                                    ? 'border-gray-200'
                                    : selectedRange === range.id && selectedColor === color.id
                                      ? 'border-bush group-hover:scale-105'
                                      : 'border-white group-hover:scale-105'
                                }`}
                                style={hasRealImage ? {} : { backgroundColor: color.hex_color }}
                              >
                                {hasRealImage && (
                                  <img 
                                    src={getCachedUrl(color.image_url)} 
                                    alt={color.name}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                                {selectedRange === range.id && selectedColor === color.id && !isOutOfStock && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow">
                                      <Check className="h-4 w-4 text-bush" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Color Name */}
                              <span className={`text-[10px] sm:text-xs text-center mt-1.5 leading-tight ${
                                isOutOfStock ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {color.name}
                              </span>
                              
                              {/* Out of Stock Badge */}
                              {isOutOfStock && (
                                <span className="text-[8px] sm:text-[9px] text-red-500 font-medium leading-tight mt-0.5">
                                  Out of stock
                                </span>
                              )}
                              
                            </button>

                            {/* Large Preview on Hover/Click */}
                            <div 
                              className={`fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden ${showPreview ? '' : 'invisible pointer-events-none'}`}
                              style={{
                                top: `${(isMobile ? mobilePosition.top : hoveredPosition.top) - 200}px`,
                                left: `${isMobile ? mobilePosition.left : hoveredPosition.left}px`,
                                transform: 'translateX(-50%)',
                                width: '180px'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {hasRealImage ? (
                                <img 
                                  src={getCachedUrl(color.image_url)} 
                                  alt={color.name}
                                  className="w-full h-32 object-cover"
                                />
                              ) : (
                                <div 
                                  className="w-full h-32"
                                  style={{ backgroundColor: color.hex_color }}
                                />
                              )}
                              <div className="p-3">
                                <p className="font-semibold text-sm text-[#1A1A1A]">{color.name}</p>
                                <p className="text-xs text-gray-500 mb-2">{range.name}</p>
                                {isOutOfStock && (
                                  <p className="text-xs text-red-500 font-medium">Out of stock</p>
                                )}
                                {isMobile && !isOutOfStock && (
                                  <button
                                    onClick={() => handleColorSelect(range.id, color.id)}
                                    className="w-full bg-bush text-white text-xs py-2 rounded-lg font-medium"
                                  >
                                    Select
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}
                </div>
              </div>
            </div>
          );
          }

          // Add "Load All Ranges" button if there are more ranges
          if (!showAllRanges && totalVisible > INITIAL_COUNT) {
            items.push(
              <button
                key="load-all-ranges"
                onClick={() => setShowAllRanges(true)}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:border-bush hover:text-bush hover:bg-bush/5 transition-all"
                data-testid="load-all-ranges-btn"
              >
                Load All Ranges ({totalVisible - INITIAL_COUNT} more)
              </button>
            );
          }

          return items;
        })()}
      </div>
    </div>
  );
};

export default FabricStep;
